// "Needs attention" (WEB_PLAN.md §5.3): overdue scheduled activities, at-risk deals (open, no activity for 30 days)
// and stores with no owner. Each block names the few that matter most and links to the Stores list filtered to the
// rest (dashboardModel.storeLinks).

import { AlarmClock, ChevronRight, TriangleAlert, UserRoundX } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { MoneyText, OwnerAvatar } from '@/features/common';
import { formatGrouped } from '@/domain/format';
import { IconBadge, SectionCard, cn } from '@/ui';
import { ActivityGlyph } from './ActivityGlyph';
import { atRiskLine, overdueLine, type NeedsAttention, type StoreLinks } from './dashboardModel';

export interface NeedsAttentionCardProps {
  data: NeedsAttention;
  links: StoreLinks;
  now: Date;
  className?: string;
}

export function NeedsAttentionCard({ data, links, now, className }: NeedsAttentionCardProps) {
  const { overdue, atRisk, unassigned } = data;
  return (
    <SectionCard title="Needs attention" padding="none" bodyClassName="divide-y divide-line overflow-hidden" className={className}>
      <Block
        icon={<AlarmClock />}
        color="#DB4038"
        title="Overdue"
        count={overdue.count}
        countLabel={`${formatGrouped(overdue.count)} overdue ${overdue.count === 1 ? 'activity' : 'activities'}`}
        empty="Nothing overdue. Every scheduled activity is on track."
        moreHref={overdue.count > 0 ? links.overdue : undefined}
        moreLabel="Follow-ups"
      >
        {overdue.items.map((item) => (
          <ItemRow key={item.id} href={item.href}>
            <ActivityGlyph type={item.type} color={item.tint} size={28} />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-medium text-fg">{item.title}</span>
              <span className={cn('truncate text-xs', item.href ? 'text-fg-muted' : 'text-fg-subtle')}>{item.storeLabel}</span>
            </span>
            <span className="flex shrink-0 flex-col items-end gap-0.5">
              <span className="text-[11px] font-semibold text-danger-text">{overdueLine(item, now)}</span>
              <OwnerAvatar user={item.user} showName size="xs" className="max-w-[9rem] text-[11px]" />
            </span>
          </ItemRow>
        ))}
      </Block>

      <Block
        icon={<TriangleAlert />}
        color="#F5B400"
        title="At-risk deals"
        count={atRisk.count}
        countLabel={`${formatGrouped(atRisk.count)} at-risk ${atRisk.count === 1 ? 'deal' : 'deals'}`}
        extra={atRisk.value > 0 ? <MoneyText value={atRisk.value} compact className="text-xs text-fg-muted" /> : undefined}
        empty="Every open store has been touched in the last 30 days."
        moreHref={atRisk.count > 0 ? links.atRisk : undefined}
        moreLabel="See all"
      >
        {atRisk.items.map((item) => (
          <ItemRow key={item.id} href={`/stores/${item.id}`}>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-medium text-fg">{item.name}</span>
              <span className="truncate text-xs text-fg-muted">{atRiskLine(item)}</span>
            </span>
            <span className="flex shrink-0 flex-col items-end gap-0.5">
              <MoneyText value={item.value} zero="—" className="text-sm font-semibold text-fg" />
              <OwnerAvatar user={item.owner} showName size="xs" className="max-w-[9rem] text-[11px]" />
            </span>
          </ItemRow>
        ))}
      </Block>

      {unassigned !== null && (
        <div className="flex items-center gap-3 px-4 py-3">
          <IconBadge icon={<UserRoundX />} color="#8C919E" size={30} />
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="text-sm font-semibold text-fg">No owner</span>
            <span className="text-xs text-fg-muted">
              {unassigned === 0
                ? 'Every store has an owner.'
                : `${formatGrouped(unassigned)} ${unassigned === 1 ? 'store has' : 'stores have'} no owner.`}
            </span>
          </div>
          {unassigned > 0 && links.unassigned && (
            <Link
              to={links.unassigned}
              className="inline-flex shrink-0 items-center gap-0.5 text-[11px] font-bold tracking-[0.06em] text-primary-text uppercase hover:underline"
            >
              Show
              <span className="sr-only"> stores with no owner</span>
              <ChevronRight aria-hidden className="size-3.5" />
            </Link>
          )}
        </div>
      )}
    </SectionCard>
  );
}

interface BlockProps {
  icon: ReactNode;
  color: string;
  title: string;
  count: number;
  countLabel: string;
  extra?: ReactNode;
  empty: string;
  moreHref?: string;
  moreLabel: string;
  children: ReactNode;
}

// A plain group under its h3, not a <section aria-label>: named sections are landmarks, and three per card would
// crowd the page's landmark list.
function Block({ icon, color, title, count, countLabel, extra, empty, moreHref, moreLabel, children }: BlockProps) {
  return (
    <div className="flex flex-col">
      <header className="flex items-center gap-3 px-4 pt-3 pb-2">
        <IconBadge icon={icon} color={count > 0 ? color : '#8C919E'} size={30} />
        <div className="flex min-w-0 flex-1 items-baseline gap-2">
          <h3 className="text-sm font-semibold text-fg">{title}</h3>
          <span
            aria-hidden
            className={cn(
              'rounded-full px-1.5 text-[11px] font-bold tabular-nums',
              count > 0 ? 'bg-surface-3 text-fg' : 'text-fg-subtle',
            )}
          >
            {formatGrouped(count)}
          </span>
          <span className="sr-only">{countLabel}</span>
          {extra}
        </div>
        {moreHref && (
          <Link
            to={moreHref}
            className="inline-flex shrink-0 items-center gap-0.5 text-[11px] font-bold tracking-[0.06em] text-primary-text uppercase hover:underline"
          >
            {moreLabel}
            <span className="sr-only"> ({title.toLowerCase()})</span>
            <ChevronRight aria-hidden className="size-3.5" />
          </Link>
        )}
      </header>
      {count === 0 ? (
        <p className="px-4 pb-3 text-xs text-fg-muted">{empty}</p>
      ) : (
        <ul className="pb-1.5">{children}</ul>
      )}
    </div>
  );
}

function ItemRow({ href, children }: { href?: string; children: ReactNode }) {
  const classes = 'flex min-w-0 items-center gap-3 px-4 py-2';
  return (
    <li>
      {href ? (
        <Link to={href} className={cn(classes, 'transition-colors hover:bg-surface-2')}>
          {children}
        </Link>
      ) : (
        <div className={classes}>{children}</div>
      )}
    </li>
  );
}
